import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, formatApiErrorDetail, downloadExport } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { PageHeader, EmptyState, useBrand, Confirm } from "../components/Shared";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { GraduationCap, Send, CheckCircle2, FileText, Eye, RotateCcw, XCircle, Ban, BarChart3, Search, Zap, CalendarDays, School as SchoolIcon, User, ClipboardList, ClipboardCheck, Clock, Trash2, History } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, ResponsiveContainer } from "recharts";
import { toast } from "sonner";
import { fmtDate, statusBadgeClass, ROLES } from "../lib/format";

const TAGLINE = "Specialists in Domestic, Commercial, and Industrial Electrical Installations & Solar Energy Solutions";
const STATUS_LABEL = { pending_ceo: "Pending CEO approval (to begin)", active: "Active — filling activities", submitted: "Pending supervisor evaluation", ceo_review: "Pending CEO final approval", validated: "Validated", rejected: "Rejected", cancelled: "Cancelled" };
const REMARK_OPTIONS = ["Successfully Completed", "Completed with guidance", "Partially completed", "Needs improvement", "Not yet completed"];
const EVENT_LABEL = { requested: "Requested", approved: "Approved to begin", submitted: "Submitted for evaluation", sent_to_ceo: "Sent to CEO for approval", returned_to_supervisor: "Returned to supervisor", returned_to_student: "Returned to student for changes", escalated_to_ceo: "Auto-escalated to CEO", returned: "Sent back for changes", validated: "Validated & signed", rejected: "Rejected", cancelled: "Cancelled", reinstated: "Reinstated", reopened: "Reopened for editing" };
const EVAL_AREAS = [
  { key: "technical", title: "Technical Competence", desc: "Ability to apply theoretical knowledge, use relevant tools and equipment, and perform assigned tasks correctly." },
  { key: "performance", title: "Work Performance", desc: "Work quality, accuracy, efficiency, and ability to meet deadlines." },
  { key: "conduct", title: "Professional Conduct", desc: "Punctuality, discipline, responsibility, teamwork, communication, adaptability, and willingness to learn." },
  { key: "problem_solving", title: "Problem-solving & Initiative", desc: "Ability to identify problems, develop practical solutions, work independently, and demonstrate creativity and initiative." },
];
const EVAL_REMARKS = ["Excellent", "Very Good", "Good", "Fair", "Poor"];
const SCORE_OPTIONS = [1, 2, 3, 4, 5];
const gradeLabel = (t) => (t >= 18 ? "Excellent" : t >= 15 ? "Very Good" : t >= 12 ? "Good" : t >= 10 ? "Fair" : "Needs Improvement");

const Timeline = ({ items, refNo }) => {
  if ((!items || items.length === 0) && !refNo) return null;
  return (
    <div className="mt-8 rounded-xl border border-slate-200 bg-slate-50/50 p-5" data-testid="internship-timeline">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2 text-sm font-heading font-semibold text-slate-800"><History className="h-4 w-4 text-[#1E3A8A]" /> Activity Timeline</div>
        {refNo && <span className="text-xs font-mono text-slate-500" data-testid="timeline-ref">{refNo}</span>}
      </div>
      <ol className="relative border-l-2 border-slate-200 ml-2 space-y-4">
        {items.map((t, i) => (
          <li key={i} className="ml-4" data-testid={`timeline-item-${i}`}>
            <span className="absolute -left-[7px] mt-1 h-3 w-3 rounded-full bg-[#1E3A8A] ring-2 ring-white" />
            <div className="text-sm font-semibold text-slate-800">{EVENT_LABEL[t.event] || t.event}</div>
            <div className="text-xs text-slate-500">{t.by ? `by ${t.by} · ` : ""}{t.at ? new Date(t.at).toLocaleString() : ""}</div>
            {t.note && <div className="text-xs text-slate-500 italic mt-0.5">“{t.note}”</div>}
          </li>
        ))}
      </ol>
    </div>
  );
};

function fmtLocal(dt) {
  return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, "0")}-${String(dt.getDate()).padStart(2, "0")}`;
}

function shiftDate(dateStr, days) {
  const d = new Date(`${dateStr}T00:00:00`);
  d.setDate(d.getDate() + days);
  return fmtLocal(d);
}

function datesInPeriod(start, end) {
  if (!start || !end) return [];
  const s = new Date(`${start}T00:00:00`);
  const e = new Date(`${end}T00:00:00`);
  if (isNaN(s.getTime()) || isNaN(e.getTime()) || e < s) return [];
  const out = [];
  for (let d = new Date(s); d <= e; d.setDate(d.getDate() + 1)) out.push(fmtLocal(d));
  return out;
}

function buildRows(form) {
  const dates = datesInPeriod(form?.period_start, form?.period_end);
  if (dates.length === 0) return form?.activities || [];
  const acts = form?.activities || [];
  // Legacy data saved under the old UTC-based code stored dates one day behind the
  // period (first key < period_start). Shift such records forward a day to realign,
  // so no entry is dropped; the correct dates persist on the next save.
  const legacy = acts.some((a) => a.date && a.date < form.period_start);
  const existing = {};
  acts.forEach((a) => { if (a.date) existing[legacy ? shiftDate(a.date, 1) : a.date] = a; });
  return dates.map((date) => ({ date, activity: existing[date]?.activity || "", remarks: existing[date]?.remarks || "" }));
}

const remarkBadgeClass = (r) => ({
  "Successfully Completed": "bg-emerald-50 text-emerald-700 border-emerald-200",
  "Completed with guidance": "bg-sky-50 text-sky-700 border-sky-200",
  "Partially completed": "bg-amber-50 text-amber-700 border-amber-200",
  "Needs improvement": "bg-orange-50 text-orange-700 border-orange-200",
  "Not yet completed": "bg-slate-100 text-slate-600 border-slate-200",
}[r] || "bg-slate-100 text-slate-600 border-slate-200");

const FormBrand = () => {
  const brand = useBrand();
  const name = "SilasTech Enterprise-Cameroon";
  const [org, setOrg] = useState({});
  useEffect(() => { api.get("/organization-info").then(({ data }) => setOrg(data || {})).catch(() => {}); }, []);
  return (
  <div className="relative overflow-hidden rounded-2xl bg-[#0B132B] text-white px-6 sm:px-8 py-6 mb-6" data-testid="internship-brand">
    <div className="absolute inset-0 pointer-events-none opacity-[0.18]" style={{ backgroundImage: "radial-gradient(circle at 15% 15%, #38bdf8 0, transparent 45%), radial-gradient(circle at 92% 85%, #f97316 0, transparent 42%)" }} />
    <div className="absolute left-0 top-0 h-full w-1.5 bg-[#F97316]" />
    <div className="relative flex items-start justify-between gap-4">
      <div className="flex items-start gap-4">
        <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-white/10 ring-1 ring-white/20 backdrop-blur overflow-hidden shrink-0">
          {brand.logo_url
            ? <img src={brand.logo_url} alt={name} className="h-full w-full object-cover" data-testid="internship-brand-logo" />
            : <Zap className="h-6 w-6 text-[#F97316]" />}
        </div>
        <div>
          <h2 className="font-heading font-extrabold text-2xl tracking-tight leading-none">{name}</h2>
          <p className="mt-1.5 text-xs sm:text-sm text-sky-200/80 italic max-w-xl leading-snug">{TAGLINE}</p>
          {org.address && <p className="mt-1 text-[11px] text-sky-200/70" data-testid="brand-address">{org.address}</p>}
          {(org.reg_no || org.email || org.phone) && <p className="mt-1 text-[11px] text-sky-200/70" data-testid="brand-contact">{[org.reg_no && `Reg. No ${org.reg_no}`, org.email && `Email: ${org.email}`, org.phone && `Tel: ${org.phone}`].filter(Boolean).join("   |   ")}</p>}
          {(org.website || org.taxpayer) && <p className="mt-0.5 text-[11px] text-sky-200/60" data-testid="brand-reg-no">{[org.website && `Web: ${org.website}`, org.taxpayer && `Taxpayer No ${org.taxpayer}`].filter(Boolean).join("   ·   ")}</p>}
        </div>
      </div>
      <div className="hidden sm:flex items-center gap-2 rounded-full bg-white/10 ring-1 ring-white/15 px-3 py-1.5 text-xs font-semibold text-sky-100 whitespace-nowrap">
        <GraduationCap className="h-4 w-4" /> Student Report
      </div>
    </div>
    <div className="relative mt-5 flex items-center gap-3">
      <div className="h-px flex-1 bg-white/15" />
      <span className="font-heading font-bold text-sm tracking-[0.22em] text-white/90 uppercase">Student Daily Activities</span>
      <div className="h-px flex-1 bg-white/15" />
    </div>
  </div>
  );
};

const InfoField = ({ icon: Icon, label, value }) => (
  <div className="flex items-center gap-3 rounded-xl border border-slate-200 bg-slate-50/60 px-4 py-3">
    <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-[#1E3A8A]/10 text-[#1E3A8A]"><Icon className="h-4 w-4" /></div>
    <div className="min-w-0">
      <div className="text-[11px] uppercase tracking-wide text-slate-400 font-semibold">{label}</div>
      <div className="text-sm font-semibold text-slate-800 truncate">{value || "—"}</div>
    </div>
  </div>
);

const InfoGrid = ({ form }) => (
  <>
    {form.ref_no && <div className="mb-2 text-xs font-mono text-slate-500" data-testid="internship-ref">Reference No: <span className="font-semibold text-slate-700">{form.ref_no}</span></div>}
    <div className="grid sm:grid-cols-3 gap-3 mb-6">
      <InfoField icon={User} label="Student" value={form.student_name} />
      <InfoField icon={SchoolIcon} label="School" value={form.school} />
      <InfoField icon={CalendarDays} label="Period" value={`${form.period_start} → ${form.period_end}`} />
    </div>
  </>
);

const StatusBanner = ({ form }) => {
  const map = {
    pending_ceo: { icon: Clock, cls: "bg-amber-50 border-amber-200 text-amber-800", text: "Your request is waiting for CEO approval. You'll be able to fill in your daily activities once it's approved." },
    submitted: { icon: Send, cls: "bg-blue-50 border-blue-200 text-blue-800", text: "Submitted — awaiting your supervisor's evaluation." },
    ceo_review: { icon: Clock, cls: "bg-indigo-50 border-indigo-200 text-indigo-800", text: "Evaluated by your supervisor — awaiting final CEO approval and signature." },
    validated: { icon: CheckCircle2, cls: "bg-emerald-50 border-emerald-200 text-emerald-800", text: `Validated by the CEO.${form.validated_by_name ? ` Signed: ${form.validated_by_name}.` : ""}` },
    rejected: { icon: XCircle, cls: "bg-red-50 border-red-200 text-red-800", text: `This request was rejected. ${form.decision_note || ""}` },
    cancelled: { icon: Ban, cls: "bg-red-50 border-red-200 text-red-800", text: `This form has been cancelled by the CEO. ${form.decision_note || ""}` },
  };
  const s = map[form.status];
  if (!s) return null;
  const Icon = s.icon;
  return <div className={`flex items-start gap-3 rounded-xl border px-4 py-3 mb-6 text-sm ${s.cls}`}><Icon className="h-4 w-4 mt-0.5 shrink-0" /><span>{s.text}</span></div>;
};

const SectionTitle = ({ icon: Icon, children, right }) => (
  <div className="flex items-center justify-between mb-3">
    <div className="flex items-center gap-2 font-heading font-semibold text-slate-800">
      <Icon className="h-4 w-4 text-[#1E3A8A]" /> {children}
    </div>
    {right}
  </div>
);

const EvaluationSection = ({ value, editable, onChange, onSave, busy }) => {
  const ev = value || {};
  const hasData = EVAL_AREAS.some((a) => (Number(ev[a.key]?.score) || 0) > 0 || (ev[a.key]?.remark || ""));
  if (!editable && !hasData) return null;
  const total = EVAL_AREAS.reduce((s, a) => s + (Number(ev[a.key]?.score) || 0), 0);
  const pct = Math.round((total / 20) * 100);
  const set = (key, field, val) => onChange({ ...ev, [key]: { ...(ev[key] || {}), [field]: val } });
  return (
    <div className="mt-8 rounded-xl border border-slate-200 bg-white p-5" data-testid="internship-evaluation">
      <div className="flex items-center gap-2 text-sm font-heading font-semibold text-slate-800 mb-1"><ClipboardCheck className="h-4 w-4 text-[#1E3A8A]" /> Student Evaluation &amp; Grading</div>
      <p className="text-xs text-slate-400 mb-4">Each area is graded on a scale of 5. The overall grade is out of 20.</p>
      <div className="rounded-xl border border-slate-200 overflow-hidden [&_thead_th]:bg-[#0B132B] [&_thead_th]:text-white [&_thead_th]:font-semibold [&_thead_th]:h-11">
        <Table>
          <TableHeader><TableRow className="border-0 hover:bg-transparent">
            <TableHead className="bg-[#0B132B] text-white">Evaluation Area</TableHead>
            <TableHead className="w-28 bg-[#0B132B] text-white">Score (/5)</TableHead>
            <TableHead className="w-44 bg-[#0B132B] text-white">Remark</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {EVAL_AREAS.map((a) => (
              <TableRow key={a.key} data-testid={`eval-row-${a.key}`}>
                <TableCell className="align-top">
                  <div className="font-semibold text-sm text-slate-800">{a.title}</div>
                  <div className="text-xs text-slate-500 mt-0.5 leading-snug">{a.desc}</div>
                </TableCell>
                <TableCell className="align-top">
                  {editable ? (
                    <Select value={ev[a.key]?.score ? String(ev[a.key].score) : ""} onValueChange={(v) => set(a.key, "score", Number(v))}>
                      <SelectTrigger data-testid={`eval-score-${a.key}`}><SelectValue placeholder="—" /></SelectTrigger>
                      <SelectContent>{SCORE_OPTIONS.map((n) => <SelectItem key={n} value={String(n)} data-testid={`eval-score-${a.key}-${n}`}>{n}</SelectItem>)}</SelectContent>
                    </Select>
                  ) : <span className="text-sm font-bold text-[#1E3A8A]">{ev[a.key]?.score ? `${ev[a.key].score}/5` : "—"}</span>}
                </TableCell>
                <TableCell className="align-top">
                  {editable ? (
                    <Select value={ev[a.key]?.remark || ""} onValueChange={(v) => set(a.key, "remark", v)}>
                      <SelectTrigger data-testid={`eval-remark-${a.key}`}><SelectValue placeholder="Select remark" /></SelectTrigger>
                      <SelectContent>{EVAL_REMARKS.map((o) => <SelectItem key={o} value={o} data-testid={`eval-remark-${a.key}-${o.replace(/\s+/g, "-").toLowerCase()}`}>{o}</SelectItem>)}</SelectContent>
                    </Select>
                  ) : <span className="text-sm text-slate-700">{ev[a.key]?.remark || "—"}</span>}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      <div className="mt-4 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-blue-100 bg-blue-50/60 px-4 py-3">
        <div className="text-sm font-semibold text-slate-700">Total Grade</div>
        <div className="flex items-center gap-3">
          <span className="text-lg font-bold text-[#1E3A8A] font-mono-num" data-testid="eval-total">{total} / 20</span>
          <span className="px-2.5 py-0.5 rounded-full text-xs font-semibold border bg-white text-slate-600" data-testid="eval-grade-label">{pct}% · {gradeLabel(total)}</span>
        </div>
      </div>
      {editable && onSave && (
        <div className="mt-3 flex justify-end">
          <Button variant="outline" data-testid="eval-save" onClick={onSave} disabled={busy}>Save evaluation</Button>
        </div>
      )}
    </div>
  );
};

const SupervisorRemarks = () => (
  <div className="mt-8 rounded-xl border border-slate-200 bg-slate-50/50 p-5" data-testid="supervisor-remarks">
    <div className="flex items-center gap-2 text-sm font-heading font-semibold text-slate-800 mb-5"><ClipboardList className="h-4 w-4 text-[#1E3A8A]" /> Supervisor's Remarks</div>
    <div className="space-y-7">
      {[0, 1, 2].map((i) => <div key={i} className="border-b border-dashed border-slate-300" />)}
    </div>
  </div>
);

const SignatureLines = () => (
  <div className="grid grid-cols-2 gap-10 mt-10 pt-2">
    {["Internship Supervisor", "CEO"].map((label) => (
      <div key={label} className="text-center">
        <div className="h-12" />
        <div className="border-t-2 border-slate-300 pt-2 text-sm font-semibold text-slate-700">{label}</div>
        <div className="text-xs text-slate-400 mt-0.5">(Signature &amp; Date)</div>
      </div>
    ))}
  </div>
);

function ActivitiesTable({ rows, editable, onChange }) {
  const update = (i, key, val) => { const next = rows.map((r, idx) => idx === i ? { ...r, [key]: val } : r); onChange(next); };
  return (
    <div className="rounded-xl border border-slate-200 overflow-hidden [&_thead_th]:bg-[#0B132B] [&_thead_th]:text-white [&_thead_th]:font-semibold [&_thead_th]:h-11 [&_tbody_tr:nth-child(even)]:bg-slate-50/70">
      <Table>
        <TableHeader><TableRow className="border-0 hover:bg-transparent">
          <TableHead className="w-32 bg-[#0B132B] text-white">Date</TableHead><TableHead className="w-[55%] bg-[#0B132B] text-white">Activity</TableHead><TableHead className="w-56 bg-[#0B132B] text-white">Remarks</TableHead>
        </TableRow></TableHeader>
        <TableBody>
          {rows.length === 0 && <TableRow><TableCell colSpan={3} className="text-slate-400 text-sm">No activities yet.</TableCell></TableRow>}
          {rows.map((r, i) => (
            <TableRow key={r.date || i} data-testid={`activity-row-${i}`}>
              <TableCell className="align-top whitespace-nowrap text-sm font-medium text-slate-700" data-testid={`activity-date-${i}`}>{fmtDate(r.date) || r.date || "—"}</TableCell>
              <TableCell className="align-top">
                {editable ? <textarea rows={1} data-testid={`activity-activity-${i}`} value={r.activity || ""} onChange={(e) => update(i, "activity", e.target.value)} placeholder="Describe the activity" className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm leading-5 ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 resize-none overflow-hidden h-9 transition-[height] duration-200 focus:h-24 focus:overflow-auto sm:focus:h-9 sm:focus:overflow-hidden" /> : <span className="whitespace-pre-wrap text-sm text-slate-700">{r.activity || "—"}</span>}
              </TableCell>
              <TableCell className="align-top">
                {editable ? (
                  <Select value={r.remarks || ""} onValueChange={(v) => update(i, "remarks", v)}>
                    <SelectTrigger data-testid={`activity-remarks-${i}`}><SelectValue placeholder="Select remark" /></SelectTrigger>
                    <SelectContent>
                      {REMARK_OPTIONS.map((o) => <SelectItem key={o} value={o} data-testid={`remark-option-${i}-${o.replace(/\s+/g, "-").toLowerCase()}`}>{o}</SelectItem>)}
                    </SelectContent>
                  </Select>
                ) : (r.remarks ? <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-semibold border ${remarkBadgeClass(r.remarks)}`}>{r.remarks}</span> : <span className="text-slate-400">—</span>)}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

function InternView({ form, refetch }) {
  const qc = useQueryClient();
  const [rows, setRows] = useState(() => buildRows(form));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { setRows(buildRows(form)); }, [form?.id, form?.status, form?.period_start, form?.period_end]);
  const editable = form?.status === "active";
  const [busy, setBusy] = useState(false);
  const totalDays = rows.length;
  const filledDays = rows.filter((r) => (r.activity || "").trim() && (r.remarks || "").trim()).length;
  const pct = totalDays ? Math.round((filledDays / totalDays) * 100) : 0;
  const dirty = editable && JSON.stringify(rows) !== JSON.stringify(buildRows(form));
  useEffect(() => {
    if (!dirty) return;
    const h = (e) => { e.preventDefault(); e.returnValue = ""; };
    window.addEventListener("beforeunload", h);
    return () => window.removeEventListener("beforeunload", h);
  }, [dirty]);

  const saveActivities = async () => {
    setBusy(true);
    try { await api.put(`/internship-forms/${form.id}/activities`, { activities: rows }); toast.success("Activities saved."); qc.invalidateQueries({ queryKey: ["internship-forms"] }); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const sendForApproval = async () => {
    if (dirty) { toast.error("Please save your activities before sending for approval."); return; }
    setBusy(true);
    try { await api.post(`/internship-forms/${form.id}/submit`); toast.success("Sent to your supervisor for evaluation."); qc.invalidateQueries({ queryKey: ["internship-forms"] }); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="bg-white rounded-2xl border border-slate-200 shadow-[0_20px_60px_-30px_rgba(2,6,23,0.35)] p-6 sm:p-8 max-w-4xl mx-auto" data-testid="internship-form-card">
      <FormBrand />
      <SectionTitle icon={User} right={<span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold border ${statusBadgeClass(form.status)}`} data-testid="internship-status">{STATUS_LABEL[form.status] || form.status}</span>}>Student Information</SectionTitle>
      <InfoGrid form={form} />

      <StatusBanner form={form} />

      <SectionTitle icon={CalendarDays} right={totalDays > 0 ? <span className="text-xs font-semibold text-slate-500" data-testid="activity-progress-label">{filledDays}/{totalDays} days</span> : null}>Daily Activities</SectionTitle>
      {totalDays > 0 && (
        <div className="mb-4" data-testid="activity-progress">
          <div className="h-2.5 w-full rounded-full bg-slate-100 overflow-hidden">
            <div className="h-full rounded-full bg-gradient-to-r from-[#1E3A8A] to-[#38bdf8] transition-all duration-500" style={{ width: `${pct}%` }} data-testid="activity-progress-bar" />
          </div>
          <div className="mt-1.5 text-xs text-slate-400">{pct}% completed{editable && filledDays < totalDays ? ` — ${totalDays - filledDays} day(s) left to fill in` : ""}</div>
        </div>
      )}
      <ActivitiesTable rows={rows} editable={editable} onChange={setRows} />

      {editable && (
        <div className="flex flex-wrap items-center gap-2 mt-5 pt-5 border-t border-slate-100">
          {dirty && <span className="text-xs text-amber-600 font-medium mr-auto" data-testid="unsaved-indicator">● Unsaved changes</span>}
          <Button variant="outline" data-testid="save-activities" onClick={saveActivities} disabled={busy || !dirty}>Save activities</Button>
          <Button className="bg-emerald-600 hover:bg-emerald-700" data-testid="send-for-approval" onClick={sendForApproval} disabled={busy}><Send className="h-4 w-4 mr-1" /> Send for Approval</Button>
        </div>
      )}

      <EvaluationSection value={form.evaluation} editable={false} onChange={() => {}} />
      <SupervisorRemarks />
      <SignatureLines />
      <Timeline items={form.timeline} refNo={form.ref_no} />
    </div>
  );
}

function ManagerView({ forms }) {
  const { user } = useAuth();
  const isCEO = user.role === ROLES.CEO;
  const isSS = user.role === ROLES.SS;
  const qc = useQueryClient();
  const [viewing, setViewing] = useState(null);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [confirmDel, setConfirmDel] = useState(null);
  const [evalDraft, setEvalDraft] = useState({});
  const [searchParams, setSearchParams] = useSearchParams();
  // Auto-open a form's dialog when arriving via ?evaluate=<id> (e.g. from the Supervisor dashboard).
  useEffect(() => {
    const id = searchParams.get("evaluate");
    if (!id) return;
    const f = forms.find((x) => x.id === id);
    if (f) {
      setViewing(f);
      setNote("");
      setSearchParams({}, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, forms]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { setEvalDraft(viewing?.evaluation || {}); }, [viewing?.id]);
  const periodOver = !!viewing?.period_over;
  const hasEval = !!(viewing?.evaluation && Object.keys(viewing.evaluation).length);
  const canEval = (isSS && viewing?.status === "submitted" && periodOver)
    || (isCEO && (["ceo_review", "validated"].includes(viewing?.status) || (viewing?.status === "submitted" && viewing?.escalated)));
  const saveEval = async () => {
    setBusy(true);
    try {
      const payload = {};
      EVAL_AREAS.forEach((a) => { payload[a.key] = { score: Number(evalDraft[a.key]?.score) || 0, remark: evalDraft[a.key]?.remark || "" }; });
      const r = await api.put(`/internship-forms/${viewing.id}/evaluation`, payload);
      toast.success("Evaluation saved.");
      if (r?.data) setViewing(r.data);
      qc.invalidateQueries({ queryKey: ["internship-forms"] });
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const q = search.trim().toLowerCase();
  const filtered = forms.filter((f) => {
    if (statusFilter !== "all" && f.status !== statusFilter) return false;
    if (!q) return true;
    return `${f.student_name || ""} ${f.school || ""}`.toLowerCase().includes(q);
  });

  const act = async (id, path, body) => {
    setBusy(true);
    try { await api.post(`/internship-forms/${id}/${path}`, body || {}); toast.success("Done."); qc.invalidateQueries({ queryKey: ["internship-forms"] }); setViewing(null); setNote(""); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const doDelete = async () => {
    if (!confirmDel) return;
    setBusy(true);
    try { await api.delete(`/internship-forms/${confirmDel.id}`); toast.success("Form deleted."); qc.invalidateQueries({ queryKey: ["internship-forms"] }); setConfirmDel(null); setViewing(null); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <>
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[220px] max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input data-testid="internship-search" className="pl-9" placeholder="Search student or school…" value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-52" data-testid="internship-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            {Object.entries(STATUS_LABEL).map(([k, v]) => <SelectItem key={k} value={k} data-testid={`status-filter-${k}`}>{v}</SelectItem>)}
          </SelectContent>
        </Select>
        <span className="text-xs text-slate-500 font-medium" data-testid="internship-result-count">{filtered.length} of {forms.length}</span>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        {forms.length === 0 ? <EmptyState text="No student forms yet." /> : filtered.length === 0 ? <EmptyState text="No forms match your search." /> : (
          <Table>
            <TableHeader><TableRow>
              <TableHead>Ref No</TableHead><TableHead>Student</TableHead><TableHead>School</TableHead><TableHead>Period</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Actions</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {filtered.map((f) => (
                <TableRow key={f.id} data-testid={`internship-manager-row-${f.id}`}>
                  <TableCell className="font-mono text-xs text-slate-500" data-testid={`internship-ref-${f.id}`}>{f.ref_no || "—"}</TableCell>
                  <TableCell className="font-medium text-slate-800">{f.student_name}</TableCell>
                  <TableCell className="text-slate-600">{f.school}</TableCell>
                  <TableCell className="text-slate-600">{f.period_start} → {f.period_end}</TableCell>
                  <TableCell><span className={`px-2 py-0.5 rounded-full text-xs font-semibold border ${statusBadgeClass(f.status)}`}>{STATUS_LABEL[f.status] || f.status}</span></TableCell>
                  <TableCell className="text-right">
                    <div className="stems-actions">
                      <Button size="icon" variant="ghost" data-testid={`internship-view-${f.id}`} onClick={() => { setViewing(f); setNote(""); }}><Eye className="h-4 w-4" /></Button>
                      {isCEO && f.status === "pending_ceo" && <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" data-testid={`internship-approve-${f.id}`} disabled={busy} onClick={() => act(f.id, "approve")}>Approve</Button>}
                      {isCEO && f.status === "ceo_review" && <Button size="sm" className="bg-[#1E3A8A] hover:bg-[#1D4ED8]" data-testid={`internship-validate-${f.id}`} disabled={busy} onClick={() => act(f.id, "validate")}>Validate & Sign</Button>}
                      {isSS && f.status === "submitted" && <Button size="sm" className="bg-[#1E3A8A] hover:bg-[#1D4ED8]" data-testid={`internship-evaluate-${f.id}`} onClick={() => { setViewing(f); setNote(""); }}>Evaluate</Button>}
                      {f.status === "validated" && <Button size="icon" variant="ghost" data-testid={`internship-pdf-${f.id}`} title="Download PDF" onClick={() => downloadExport(`/export/internship-form/${f.id}/pdf`, `student_${(f.student_name || f.id).replace(/\s+/g, "_")}.pdf`)}><FileText className="h-4 w-4" /></Button>}
                      {isCEO && ["validated", "rejected"].includes(f.status) && <Button size="sm" variant="outline" data-testid={`internship-reopen-${f.id}`} disabled={busy} onClick={() => act(f.id, "reopen")}><RotateCcw className="h-4 w-4 mr-1" /> Reopen</Button>}
                      {isCEO && f.overdue && !["validated", "rejected"].includes(f.status) && <Button size="sm" variant="outline" data-testid={`internship-reinstate-${f.id}`} onClick={() => act(f.id, "reinstate")}><RotateCcw className="h-4 w-4 mr-1" /> Reinstate</Button>}
                      {isCEO && !["cancelled", "validated"].includes(f.status) && <Button size="icon" variant="ghost" className="text-amber-700 hover:text-amber-800 hover:bg-amber-50" data-testid={`internship-cancel-${f.id}`} title="Cancel form" onClick={() => { setViewing(f); setNote(""); }}><Ban className="h-4 w-4" /></Button>}
                      {isCEO && <Button size="icon" variant="ghost" className="text-red-600 hover:text-red-700 hover:bg-red-50" data-testid={`internship-delete-${f.id}`} title="Delete form" onClick={() => setConfirmDel(f)}><Trash2 className="h-4 w-4" /></Button>}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      <Dialog open={!!viewing} onOpenChange={(o) => { if (!o) setViewing(null); }}>
        <DialogContent className="max-w-3xl" data-testid="internship-view-dialog">
          <DialogHeader><DialogTitle>Student Form — {viewing?.student_name}</DialogTitle>
            <DialogDescription>{viewing?.school} · {viewing?.period_start} → {viewing?.period_end}</DialogDescription></DialogHeader>
          {viewing && (
            <div>
              <FormBrand />
              <InfoGrid form={viewing} />
              <SectionTitle icon={CalendarDays}>Daily Activities</SectionTitle>
              <ActivitiesTable rows={viewing.activities || []} editable={false} onChange={() => {}} />
              {viewing.decision_note && <p className="text-sm text-slate-500 mt-3">Note: {viewing.decision_note}</p>}
              <EvaluationSection value={canEval ? evalDraft : viewing.evaluation} editable={canEval} onChange={setEvalDraft} onSave={saveEval} busy={busy} />
              <SupervisorRemarks />
              <SignatureLines />
              <Timeline items={viewing.timeline} refNo={viewing.ref_no} />
            </div>
          )}
          <DialogFooter className="flex-wrap gap-2">
            {isCEO && viewing?.status === "pending_ceo" && (
              <>
                <Input placeholder="Rejection reason (optional)" value={note} onChange={(e) => setNote(e.target.value)} className="max-w-xs" data-testid="internship-note" />
                <Button variant="outline" className="text-red-600" data-testid="internship-reject" disabled={busy} onClick={() => act(viewing.id, "reject", { note })}><XCircle className="h-4 w-4 mr-1" /> Reject</Button>
                <Button className="bg-emerald-600 hover:bg-emerald-700" data-testid="internship-approve-dlg" disabled={busy} onClick={() => act(viewing.id, "approve")}><CheckCircle2 className="h-4 w-4 mr-1" /> Approve</Button>
              </>
            )}
            {isSS && viewing?.status === "submitted" && (
              <>
                {!periodOver && <span className="text-xs text-amber-600 font-medium self-center mr-auto" data-testid="eval-locked-note">Evaluation opens on {viewing?.period_end} (after the internship ends)</span>}
                <Input placeholder="Note to student (optional)" value={note} onChange={(e) => setNote(e.target.value)} className="max-w-xs" data-testid="internship-note" />
                <Button variant="outline" data-testid="supervisor-return" disabled={busy} onClick={() => act(viewing.id, "supervisor-return", { note })}>Send back to student</Button>
                <Button className="bg-[#1E3A8A] hover:bg-[#1D4ED8]" data-testid="send-to-ceo" disabled={busy || !hasEval} title={!hasEval ? "Complete the evaluation first" : ""} onClick={() => act(viewing.id, "send-to-ceo")}><Send className="h-4 w-4 mr-1" /> Send to CEO</Button>
              </>
            )}
            {isCEO && viewing?.status === "ceo_review" && (
              <>
                <Input placeholder="Note (optional)" value={note} onChange={(e) => setNote(e.target.value)} className="max-w-xs" data-testid="internship-note" />
                <Button variant="outline" data-testid="internship-return" disabled={busy} onClick={() => act(viewing.id, "return", { note })}>Send back to supervisor</Button>
                <Button className="bg-[#1E3A8A] hover:bg-[#1D4ED8]" data-testid="internship-validate-dlg" disabled={busy} onClick={() => act(viewing.id, "validate", { note })}><CheckCircle2 className="h-4 w-4 mr-1" /> Approve & Sign</Button>
              </>
            )}
            {isCEO && viewing?.status === "submitted" && viewing?.escalated && (
              <Button className="bg-[#1E3A8A] hover:bg-[#1D4ED8]" data-testid="internship-validate-escalated" disabled={busy} onClick={() => act(viewing.id, "validate", { note })}><CheckCircle2 className="h-4 w-4 mr-1" /> Approve & Sign</Button>
            )}
            {isCEO && !["cancelled", "validated"].includes(viewing?.status) && (
              <Confirm onConfirm={() => act(viewing.id, "cancel", { note })} title="Cancel this student form?"
                description="The form will be marked cancelled and the student can no longer edit it. The record is kept and you can reopen it later if needed."
                actionLabel="Cancel form"
                trigger={<Button variant="outline" className="text-amber-700 border-amber-300 hover:bg-amber-50" data-testid="internship-cancel"><Ban className="h-4 w-4 mr-1" /> Cancel form</Button>} />
            )}
            {viewing?.status === "validated" && <Button className="bg-[#1E3A8A] hover:bg-[#1D4ED8]" data-testid="internship-pdf-dlg" onClick={() => downloadExport(`/export/internship-form/${viewing.id}/pdf`, `student_${(viewing.student_name || viewing.id).replace(/\s+/g, "_")}.pdf`)}><FileText className="h-4 w-4 mr-1" /> Download PDF</Button>}
            {isCEO && ["validated", "rejected", "cancelled"].includes(viewing?.status) && <Button variant="outline" data-testid="internship-reopen-dlg" disabled={busy} onClick={() => act(viewing.id, "reopen")}><RotateCcw className="h-4 w-4 mr-1" /> Reopen for editing</Button>}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!confirmDel} onOpenChange={(o) => { if (!o) setConfirmDel(null); }}>
        <DialogContent className="max-w-md" data-testid="internship-delete-dialog">
          <DialogHeader><DialogTitle>Delete student form?</DialogTitle>
            <DialogDescription>This permanently removes {confirmDel?.student_name}'s form and all its activities. This cannot be undone.</DialogDescription></DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="outline" data-testid="internship-delete-cancel" onClick={() => setConfirmDel(null)}>Cancel</Button>
            <Button className="bg-red-600 hover:bg-red-700" data-testid="internship-delete-confirm" disabled={busy} onClick={doDelete}><Trash2 className="h-4 w-4 mr-1" /> Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default function InternshipForm() {
  const { user } = useAuth();
  const isIntern = user.role === ROLES.INTERN;
  const isCEO = user.role === ROLES.CEO;
  const [analyticsOpen, setAnalyticsOpen] = useState(false);
  const { data: forms = [], refetch } = useQuery({ queryKey: ["internship-forms"], queryFn: () => api.get("/internship-forms").then((r) => r.data), refetchInterval: 12000, refetchOnWindowFocus: true });

  return (
    <div data-testid="internship-page">
      <PageHeader title="Students" subtitle={isIntern ? "Your student report form" : "Student report forms"}>
        {isCEO && (
          <>
            <Button variant="outline" data-testid="grade-analytics-btn" onClick={() => setAnalyticsOpen(true)}>
              <BarChart3 className="h-4 w-4 mr-2" /> Grade Analytics
            </Button>
            <Button variant="outline" data-testid="students-summary-pdf" onClick={() => downloadExport("/export/internship-forms/summary.pdf", "students_evaluation_summary.pdf")}>
              <FileText className="h-4 w-4 mr-2" /> Students Summary
            </Button>
          </>
        )}
        <GraduationCap className="h-6 w-6 text-[#1E3A8A]" />
      </PageHeader>
      {isIntern ? (
        forms.length === 0
          ? <EmptyState title="No student form yet" description="Request your student form from the Dashboard to get started." />
          : <InternView form={forms[0]} refetch={refetch} />
      ) : (
        <ManagerView forms={forms} />
      )}
      {isCEO && <GradeAnalyticsDialog open={analyticsOpen} onClose={() => setAnalyticsOpen(false)} />}
    </div>
  );
}

function GradeAnalyticsDialog({ open, onClose }) {
  const { data } = useQuery({ queryKey: ["internship-analytics"], queryFn: () => api.get("/internship-forms/analytics").then((r) => r.data), enabled: open });
  const Chart = ({ title, rows }) => (
    <div>
      <div className="text-sm font-semibold text-slate-700 mb-2">{title}</div>
      {(!rows || rows.length === 0) ? <p className="text-sm text-slate-400 py-6 text-center">No graded students yet.</p> : (
        <ResponsiveContainer width="100%" height={Math.max(120, rows.length * 44)}>
          <BarChart data={rows} layout="vertical" margin={{ left: 8, right: 24, top: 4, bottom: 4 }}>
            <CartesianGrid strokeDasharray="3 3" horizontal={false} />
            <XAxis type="number" domain={[0, 20]} tick={{ fontSize: 11 }} />
            <YAxis type="category" dataKey="name" width={120} tick={{ fontSize: 11 }} />
            <Tooltip formatter={(v, n, p) => [`${v} / 20 (${p.payload.count} student${p.payload.count === 1 ? "" : "s"})`, "Avg score"]} />
            <Bar dataKey="avg" radius={[0, 6, 6, 0]} fill="#1E3A8A" />
          </BarChart>
        </ResponsiveContainer>
      )}
    </div>
  );
  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="grade-analytics-dialog">
        <DialogHeader><DialogTitle>Grade Analytics</DialogTitle>
          <DialogDescription>Average student evaluation score (out of 20){data ? ` · ${data.graded_count} graded · overall avg ${data.overall_avg}/20` : ""}</DialogDescription></DialogHeader>
        <div className="space-y-6">
          <Chart title="Average score by supervisor" rows={data?.by_supervisor} />
          <Chart title="Average score by school" rows={data?.by_school} />
        </div>
      </DialogContent>
    </Dialog>
  );
}
